/**
 * AttachmentViewer - Universal attachment viewer for Epicor files
 *
 * Supports:
 * - Images (jpg, png, gif, etc.) - Inline preview with zoom
 * - PDFs - Embedded viewer using browser's native PDF renderer
 * - Spreadsheets (xlsx, xls, csv) - Download with preview info
 * - Documents (doc, docx, txt) - Download with preview info
 * - CAD files (dwg, dxf) - Download only with metadata display
 */

import { useState, useEffect } from 'react'
import {
  X, Download, ExternalLink, FileText, Image, FileSpreadsheet,
  File, Loader2, ZoomIn, ZoomOut, RotateCw, Maximize2, Box, Mail, Paperclip, User, Calendar, Check,
  Upload, UploadCloud, AlertCircle, CheckCircle
} from 'lucide-react'
import EmailComposeModal from '../artifacts/EmailComposeModal'

// File type icons
const FileTypeIcon = ({ extension, className = "w-8 h-8" }) => {
  const ext = extension?.toLowerCase() || ''

  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'tif'].includes(ext)) {
    return <Image className={`${className} text-green-500`} />
  }
  if (ext === 'pdf') {
    return <FileText className={`${className} text-red-500`} />
  }
  if (ext === 'msg') {
    return <Mail className={`${className} text-blue-600`} />
  }
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return <FileSpreadsheet className={`${className} text-green-600`} />
  }
  if (['doc', 'docx', 'txt'].includes(ext)) {
    return <FileText className={`${className} text-blue-500`} />
  }
  if (['dwg', 'dxf', 'step', 'stp', 'iges', 'igs'].includes(ext)) {
    return <Box className={`${className} text-purple-500`} />
  }
  return <File className={`${className} text-gray-500`} />
}

/**
 * Fetch a file with authentication and return a blob URL
 * This is needed because <img src> and <iframe src> don't send auth headers
 */
async function fetchAuthenticatedBlobUrl(url) {
  const token = localStorage.getItem('amtrend_token')
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(errorText || `HTTP ${response.status}`)
  }
  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

// Image Viewer with zoom/pan
function ImageViewer({ url, filename }) {
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [blobUrl, setBlobUrl] = useState(null)

  // Fetch image with auth token
  useEffect(() => {
    let isMounted = true
    setLoading(true)
    setError(false)

    fetchAuthenticatedBlobUrl(url)
      .then(objectUrl => {
        if (isMounted) {
          setBlobUrl(objectUrl)
        }
      })
      .catch(err => {
        console.error('Failed to load image:', err)
        if (isMounted) {
          setError(true)
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
      // Revoke the blob URL when unmounting
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [url])

  const handleZoomIn = () => setZoom(z => Math.min(z + 0.25, 3))
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, 0.5))
  const handleRotate = () => setRotation(r => (r + 90) % 360)
  const handleReset = () => { setZoom(1); setRotation(0) }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
        <span className="text-sm text-gray-600 truncate">{filename}</span>
        <div className="flex items-center gap-2">
          <button onClick={handleZoomOut} className="p-1.5 hover:bg-gray-200 rounded" title="Zoom Out">
            <ZoomOut size={18} />
          </button>
          <span className="text-sm text-gray-600 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={handleZoomIn} className="p-1.5 hover:bg-gray-200 rounded" title="Zoom In">
            <ZoomIn size={18} />
          </button>
          <button onClick={handleRotate} className="p-1.5 hover:bg-gray-200 rounded" title="Rotate">
            <RotateCw size={18} />
          </button>
          <button onClick={handleReset} className="p-1.5 hover:bg-gray-200 rounded" title="Reset">
            <Maximize2 size={18} />
          </button>
        </div>
      </div>

      {/* Image container */}
      <div className="flex-1 overflow-auto bg-gray-900 flex items-center justify-center p-4">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}
        {error ? (
          <div className="text-gray-400 text-center">
            <Image className="w-16 h-16 mx-auto mb-2 opacity-50" />
            <p>Failed to load image</p>
          </div>
        ) : blobUrl ? (
          <img
            src={blobUrl}
            alt={filename}
            className={`max-w-full max-h-full object-contain transition-transform duration-200 ${loading ? 'opacity-0' : 'opacity-100'}`}
            style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
            onLoad={() => setLoading(false)}
            onError={() => { setLoading(false); setError(true) }}
          />
        ) : null}
      </div>
    </div>
  )
}

// PDF Viewer using iframe with authenticated blob URL
function PDFViewer({ url, filename }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [blobUrl, setBlobUrl] = useState(null)

  // Fetch PDF with auth token
  useEffect(() => {
    let isMounted = true
    setLoading(true)
    setError(false)

    fetchAuthenticatedBlobUrl(url)
      .then(objectUrl => {
        if (isMounted) {
          setBlobUrl(objectUrl)
        }
      })
      .catch(err => {
        console.error('Failed to load PDF:', err)
        if (isMounted) {
          setError(true)
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [url])

  // Handle authenticated download
  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('amtrend_token')
      const response = await fetch(`${url}?download=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()
      const downloadUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      console.error('Download error:', err)
      alert('Failed to download file')
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-red-500" />
          <span className="text-sm text-gray-600 truncate">{filename}</span>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-1 text-sm text-amtrend-blue hover:underline"
        >
          <Download size={16} />
          Download
        </button>
      </div>

      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-amtrend-blue animate-spin mx-auto mb-2" />
              <p className="text-gray-600">Loading PDF...</p>
            </div>
          </div>
        )}
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-600 mb-4">Unable to preview PDF in browser</p>
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amtrend-blue text-white rounded-lg hover:bg-blue-700"
              >
                <Download size={18} />
                Download PDF
              </button>
            </div>
          </div>
        ) : blobUrl ? (
          <iframe
            src={blobUrl}
            className={`w-full h-full border-0 ${loading ? 'invisible' : 'visible'}`}
            title={filename}
            onLoad={() => setLoading(false)}
            onError={() => { setLoading(false); setError(true) }}
          />
        ) : null}
      </div>
    </div>
  )
}

// MSG (Outlook Email) Viewer
function MSGViewer({ xfileRefNum, filename }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [emailData, setEmailData] = useState(null)

  useEffect(() => {
    const fetchMsgContent = async () => {
      try {
        const token = localStorage.getItem('amtrend_token')
        const response = await fetch(`/api/attachments/msg/${xfileRefNum}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (!response.ok) {
          const errText = await response.text()
          throw new Error(errText || 'Failed to parse MSG file')
        }
        const data = await response.json()
        setEmailData(data)
      } catch (err) {
        console.error('MSG parse error:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchMsgContent()
  }, [xfileRefNum])

  // Download handler
  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('amtrend_token')
      const response = await fetch(`/api/attachments/file/${xfileRefNum}?download=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()
      const downloadUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      console.error('Download error:', err)
      alert('Failed to download file')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-amtrend-blue animate-spin mx-auto mb-2" />
          <p className="text-gray-600">Parsing email...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 bg-gray-50">
        <Mail className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-600 mb-2">Unable to preview MSG file</p>
        <p className="text-sm text-gray-400 mb-4">{error}</p>
        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-2 px-4 py-2 bg-amtrend-blue text-white rounded-lg hover:bg-blue-700"
        >
          <Download size={18} />
          Download MSG File
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Email Header */}
      <div className="border-b bg-gray-50 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{emailData.subject}</h2>

        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <User size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-gray-500">From:</span>{' '}
              <span className="text-gray-800 font-medium">{emailData.sender}</span>
              {emailData.sender_email && (
                <span className="text-gray-500 ml-1">&lt;{emailData.sender_email}&gt;</span>
              )}
            </div>
          </div>

          {emailData.to && (
            <div className="flex items-start gap-2">
              <User size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-500">To:</span>{' '}
                <span className="text-gray-800">{emailData.to}</span>
              </div>
            </div>
          )}

          {emailData.cc && (
            <div className="flex items-start gap-2">
              <User size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-500">CC:</span>{' '}
                <span className="text-gray-800">{emailData.cc}</span>
              </div>
            </div>
          )}

          {emailData.date && (
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-500">Date:</span>{' '}
              <span className="text-gray-800">{emailData.date}</span>
            </div>
          )}

          {emailData.attachments && emailData.attachments.length > 0 && (
            <div className="flex items-start gap-2">
              <Paperclip size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-500">Attachments:</span>{' '}
                <span className="text-gray-800">
                  {emailData.attachments.map((a, i) => (
                    <span key={i} className="inline-flex items-center bg-gray-200 rounded px-2 py-0.5 text-xs mr-1 mb-1">
                      {a.filename}
                    </span>
                  ))}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-3 flex justify-end">
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-1 text-sm text-amtrend-blue hover:underline"
          >
            <Download size={14} />
            Download Original
          </button>
        </div>
      </div>

      {/* Email Body */}
      <div className="flex-1 overflow-auto p-4">
        {emailData.body_html ? (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: emailData.body_html }}
          />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
            {emailData.body_text || '(No message body)'}
          </pre>
        )}
      </div>
    </div>
  )
}

// Download-only viewer for unsupported file types
function DownloadViewer({ metadata, url }) {
  const { filename, extension, description, doc_type_id, is_cad, is_spreadsheet, is_document } = metadata
  const [downloading, setDownloading] = useState(false)

  // Handle authenticated download
  const handleDownload = async () => {
    setDownloading(true)
    try {
      const token = localStorage.getItem('amtrend_token')
      const response = await fetch(`${url}?download=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()
      const downloadUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      console.error('Download error:', err)
      alert('Failed to download file')
    } finally {
      setDownloading(false)
    }
  }

  // Handle authenticated open in new tab
  const handleOpenNewTab = async () => {
    try {
      const token = localStorage.getItem('amtrend_token')
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!response.ok) throw new Error('Failed to fetch file')
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      window.open(blobUrl, '_blank')
    } catch (err) {
      console.error('Open error:', err)
      alert('Failed to open file')
    }
  }

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 bg-gray-50">
      <FileTypeIcon extension={extension} className="w-20 h-20 mb-4" />

      <h3 className="text-lg font-medium text-gray-800 mb-1">{filename}</h3>
      {description && (
        <p className="text-sm text-gray-500 mb-2">{description}</p>
      )}
      {doc_type_id && (
        <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded mb-4">
          {doc_type_id}
        </span>
      )}

      {/* File type specific messaging */}
      <div className="text-center text-gray-600 text-sm mb-6 max-w-md">
        {is_cad && (
          <p>CAD files require specialized software to view. Download the file to open it in AutoCAD, SolidWorks, or another CAD viewer.</p>
        )}
        {is_spreadsheet && (
          <p>Spreadsheet files can be opened in Microsoft Excel, Google Sheets, or other spreadsheet applications.</p>
        )}
        {is_document && (
          <p>Document files can be opened in Microsoft Word, Google Docs, or other word processors.</p>
        )}
        {!is_cad && !is_spreadsheet && !is_document && (
          <p>This file type cannot be previewed in the browser. Download to view.</p>
        )}
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-6 py-3 bg-amtrend-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {downloading ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
          {downloading ? 'Downloading...' : 'Download File'}
        </button>
        <button
          onClick={handleOpenNewTab}
          className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ExternalLink size={20} />
          Open in New Tab
        </button>
      </div>
    </div>
  )
}

/**
 * Main AttachmentViewer component
 *
 * Props:
 * - xfileRefNum: The Epicor XFileRefNum to view
 * - onClose: Callback when viewer is closed
 * - metadata: Pre-fetched metadata (optional - will fetch if not provided)
 */
export default function AttachmentViewer({ xfileRefNum, onClose, metadata: initialMetadata }) {
  const [metadata, setMetadata] = useState(initialMetadata)
  const [loading, setLoading] = useState(!initialMetadata)
  const [error, setError] = useState(null)

  // Fetch metadata if not provided
  useEffect(() => {
    if (!initialMetadata && xfileRefNum) {
      const fetchMetadata = async () => {
        try {
          const token = localStorage.getItem('amtrend_token')
          const response = await fetch(`/api/attachments/metadata/${xfileRefNum}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          if (!response.ok) throw new Error('Failed to fetch metadata')
          const data = await response.json()
          setMetadata(data)
        } catch (err) {
          setError(err.message)
        } finally {
          setLoading(false)
        }
      }
      fetchMetadata()
    }
  }, [xfileRefNum, initialMetadata])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-amtrend-blue animate-spin mx-auto mb-2" />
          <p className="text-gray-600">Loading attachment...</p>
        </div>
      </div>
    )
  }

  if (error || !metadata) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <File className="w-16 h-16 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-600 mb-2">Failed to load attachment</p>
          <p className="text-sm text-gray-400">{error}</p>
          {onClose && (
            <button onClick={onClose} className="mt-4 px-4 py-2 text-amtrend-blue hover:underline">
              Close
            </button>
          )}
        </div>
      </div>
    )
  }

  const { view_url, filename, is_image, is_pdf, can_preview_inline, extension } = metadata
  const is_msg = extension?.toLowerCase() === 'msg'

  // Render appropriate viewer
  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header with close button */}
      {onClose && (
        <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
          <div className="flex items-center gap-2">
            <FileTypeIcon extension={metadata.extension} className="w-5 h-5" />
            <span className="font-medium text-gray-800">{filename}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {is_image ? (
          <ImageViewer url={view_url} filename={filename} />
        ) : is_pdf ? (
          <PDFViewer url={view_url} filename={filename} />
        ) : is_msg ? (
          <MSGViewer xfileRefNum={metadata.xfile_ref_num} filename={filename} />
        ) : (
          <DownloadViewer metadata={metadata} url={view_url} />
        )}
      </div>
    </div>
  )
}

/**
 * Compact attachment card for lists
 */
export function AttachmentCard({ attachment, onClick }) {
  const { filename, extension, description, doc_type_id, is_image, is_pdf, xfile_ref_num } = attachment
  const is_msg = extension?.toLowerCase() === 'msg'
  const can_preview = is_image || is_pdf || is_msg

  return (
    <button
      onClick={() => onClick?.(attachment)}
      className="flex items-center gap-3 p-3 w-full text-left bg-white border border-gray-200 rounded-lg hover:border-amtrend-blue hover:shadow-sm transition-all"
    >
      <FileTypeIcon extension={extension} className="w-10 h-10 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-800 truncate">{filename}</p>
        {description && (
          <p className="text-sm text-gray-500 truncate">{description}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          {doc_type_id && (
            <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
              {doc_type_id}
            </span>
          )}
          <span className="text-xs text-gray-400 uppercase">{extension}</span>
          {can_preview && (
            <span className="text-xs text-green-600">Preview available</span>
          )}
        </div>
      </div>
    </button>
  )
}

/**
 * Simple attachment list (for small sets)
 * Auto-detects if attachments have line-level data and uses appropriate view
 */
export function AttachmentList({ attachments, title, onEmailRequest, orderNum, tableName, recordKey1, recordKey2 }) {
  const [selectedAttachment, setSelectedAttachment] = useState(null)

  if (!attachments || attachments.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <File className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No attachments found</p>
      </div>
    )
  }

  // Check if attachments have line-level data (for orders)
  const hasLineData = attachments.some(att => att.line_num !== null && att.line_num !== undefined)
  const hasImages = attachments.some(att => att.is_image)

  // If has line data OR more than 15 attachments, use the enhanced browser
  // This gives access to the Gallery view for line-grouped images
  if (attachments.length > 15 || (hasLineData && hasImages)) {
    return <AttachmentBrowser attachments={attachments} title={title} onEmailRequest={onEmailRequest} orderNum={orderNum} tableName={tableName} recordKey1={recordKey1} recordKey2={recordKey2} />
  }

  return (
    <div>
      {title && (
        <h3 className="text-sm font-medium text-gray-700 mb-3">{title}</h3>
      )}
      <div className="space-y-2">
        {attachments.map((att) => (
          <AttachmentCard
            key={att.xfile_ref_num}
            attachment={att}
            onClick={setSelectedAttachment}
          />
        ))}
      </div>

      {/* Viewer modal */}
      {selectedAttachment && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] overflow-hidden">
            <AttachmentViewer
              xfileRefNum={selectedAttachment.xfile_ref_num}
              metadata={{
                ...selectedAttachment,
                view_url: `/api/attachments/file/${selectedAttachment.xfile_ref_num}`,
                download_url: `/api/attachments/file/${selectedAttachment.xfile_ref_num}?download=true`,
              }}
              onClose={() => setSelectedAttachment(null)}
            />
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Enhanced Attachment Browser - for large attachment sets
 * Features:
 * - Tabs by document type
 * - Grid view for images (grouped by order line when available)
 * - Gallery view for images grouped by line number
 * - Search/filter
 * - Download by category
 * - Email selected photos (when onEmailRequest is provided)
 */
export function AttachmentBrowser({ attachments, title, onEmailRequest, orderNum, tableName, recordKey1, recordKey2 }) {
  const [selectedAttachment, setSelectedAttachment] = useState(null)
  const [activeTab, setActiveTab] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [selectedForEmail, setSelectedForEmail] = useState([])  // For email selection (any attachment type)
  const [showEmailModal, setShowEmailModal] = useState(false)  // Email compose modal
  const [showUploadModal, setShowUploadModal] = useState(false)  // Upload modal

  // Check if attachments have line-level data
  // Check for line_num being set (number) or level being 'line'
  const hasLineData = attachments.some(att =>
    (att.line_num !== null && att.line_num !== undefined) || att.level === 'line'
  )
  const hasHeaderAndLine = hasLineData && attachments.some(att => att.level === 'header')
  const hasImages = attachments.some(att => att.is_image)

  // Debug log to help troubleshoot
  console.log('AttachmentBrowser:', {
    total: attachments.length,
    hasLineData,
    hasImages,
    sampleAtt: attachments[0],
    lineLevelCount: attachments.filter(a => a.level === 'line').length,
    headerLevelCount: attachments.filter(a => a.level === 'header').length,
  })

  // Default to 'gallery' view if we have line data with images (for line-grouped photo display)
  const defaultViewMode = (hasLineData && hasImages) ? 'gallery' : 'list'
  const [viewMode, setViewMode] = useState(defaultViewMode) // 'list', 'grid', or 'gallery'

  // Group attachments by document type
  const groupedByType = attachments.reduce((acc, att) => {
    const type = att.doc_type_id || 'OTHER'
    if (!acc[type]) acc[type] = []
    acc[type].push(att)
    return acc
  }, {})

  // Get sorted type tabs (by count, descending)
  const typeTabs = Object.entries(groupedByType)
    .sort((a, b) => b[1].length - a[1].length)
    .map(([type, items]) => ({ type, count: items.length }))

  // Filter attachments based on active tab and search
  const filteredAttachments = attachments.filter(att => {
    // Tab filter
    if (activeTab !== 'all' && (att.doc_type_id || 'OTHER') !== activeTab) {
      return false
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const filename = (att.filename || '').toLowerCase()
      const desc = (att.description || '').toLowerCase()
      const docType = (att.doc_type_id || '').toLowerCase()
      return filename.includes(query) || desc.includes(query) || docType.includes(query)
    }
    return true
  })

  // Separate images for grid view
  const imageAttachments = filteredAttachments.filter(att => att.is_image)
  const nonImageAttachments = filteredAttachments.filter(att => !att.is_image)

  // Group attachments by line (for gallery view)
  const groupedByLine = filteredAttachments.reduce((acc, att) => {
    // Determine the grouping key:
    // - 'header' for header-level attachments (level='header' or no line_num)
    // - 'line-N' for line-level attachments
    let key, level
    if (att.level === 'line' || (att.line_num !== null && att.line_num !== undefined && att.line_num !== 'null')) {
      key = `line-${att.line_num}`
      level = 'line'
    } else {
      key = 'header'
      level = 'header'
    }

    if (!acc[key]) {
      acc[key] = {
        key,
        level,
        line_num: att.line_num,
        part_num: att.part_num || '',
        xpart_num: att.xpart_num || '',  // Customer part number
        line_desc: att.line_desc || '',
        attachments: []
      }
    }
    acc[key].attachments.push(att)
    return acc
  }, {})

  // Sort groups: header first, then lines by number
  const lineGroups = Object.values(groupedByLine).sort((a, b) => {
    if (a.level === 'header') return -1
    if (b.level === 'header') return 1
    return (a.line_num || 0) - (b.line_num || 0)
  })

  // Toggle attachment selection (for email)
  const toggleSelect = (att) => {
    setSelectedForEmail(prev => {
      const isSelected = prev.some(p => p.xfile_ref_num === att.xfile_ref_num)
      if (isSelected) {
        return prev.filter(p => p.xfile_ref_num !== att.xfile_ref_num)
      } else {
        return [...prev, att]
      }
    })
  }

  // Handle email selected attachments - opens the email compose modal
  const handleEmailSelected = () => {
    if (selectedForEmail.length === 0) return
    setShowEmailModal(true)
  }

  // Handle email modal success - clear selection
  const handleEmailSuccess = () => {
    setSelectedForEmail([])
    setShowEmailModal(false)
  }

  // Get order number for email (from prop or parse from title)
  const getOrderNum = () => {
    if (orderNum) return orderNum
    const orderMatch = title?.match(/Order\s+(\d+)/i)
    return orderMatch ? parseInt(orderMatch[1], 10) : null
  }

  // Download category handler
  const handleDownloadCategory = async (docType) => {
    const toDownload = docType === 'all'
      ? filteredAttachments
      : attachments.filter(a => (a.doc_type_id || 'OTHER') === docType)

    if (toDownload.length === 0) return

    setDownloading(true)
    const token = localStorage.getItem('amtrend_token')

    for (const att of toDownload) {
      try {
        const response = await fetch(`/api/attachments/file/${att.xfile_ref_num}?download=true`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.ok) {
          const blob = await response.blob()
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = att.filename
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }
        // Small delay between downloads
        await new Promise(r => setTimeout(r, 300))
      } catch (err) {
        console.error(`Failed to download ${att.filename}:`, err)
      }
    }
    setDownloading(false)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b bg-gray-50 p-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800">
            {title || 'Attachments'}
            <span className="text-gray-500 font-normal ml-2">({attachments.length} total)</span>
          </h3>
          <div className="flex items-center gap-2">
            {/* Email selected button - show when attachments are selected */}
            {selectedForEmail.length > 0 && (
              <button
                onClick={handleEmailSelected}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-amtrend-blue text-white rounded hover:bg-blue-700 transition-colors"
              >
                <Mail size={12} />
                Email {selectedForEmail.length} selected
              </button>
            )}
            {/* View mode toggle */}
            <div className="flex border rounded overflow-hidden">
              <button
                onClick={() => setViewMode('list')}
                className={`px-2 py-1 text-xs ${viewMode === 'list' ? 'bg-amtrend-blue text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
              >
                List
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`px-2 py-1 text-xs ${viewMode === 'grid' ? 'bg-amtrend-blue text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
              >
                Grid
              </button>
              {/* Show Gallery option when we have line data (always show if available, even if no images in current filter) */}
              {hasLineData && (
                <button
                  onClick={() => setViewMode('gallery')}
                  className={`px-2 py-1 text-xs ${viewMode === 'gallery' ? 'bg-amtrend-blue text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                >
                  Gallery
                </button>
              )}
            </div>
            {/* Upload button */}
            {tableName && recordKey1 && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                <Upload size={12} />
                Upload
              </button>
            )}
            {/* Download current view */}
            <button
              onClick={() => handleDownloadCategory(activeTab)}
              disabled={downloading || filteredAttachments.length === 0}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100 disabled:opacity-50"
            >
              {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              Download {activeTab === 'all' ? 'All' : activeTab}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <input
            type="text"
            placeholder="Search attachments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border rounded-lg pl-8 focus:outline-none focus:ring-2 focus:ring-amtrend-blue/50"
          />
          <svg className="w-4 h-4 text-gray-400 absolute left-2.5 top-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Type tabs */}
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-2 py-1 text-xs rounded-full transition-colors ${
              activeTab === 'all'
                ? 'bg-amtrend-blue text-white'
                : 'bg-white border text-gray-600 hover:bg-gray-100'
            }`}
          >
            All ({attachments.length})
          </button>
          {typeTabs.map(({ type, count }) => (
            <button
              key={type}
              onClick={() => setActiveTab(type)}
              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                activeTab === type
                  ? 'bg-amtrend-blue text-white'
                  : 'bg-white border text-gray-600 hover:bg-gray-100'
              }`}
            >
              {type} ({count})
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-3">
        {filteredAttachments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <File className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No attachments match your filter</p>
          </div>
        ) : viewMode === 'gallery' ? (
          /* Gallery view - grouped by line number */
          <div className="space-y-4">
            {lineGroups.map((group) => {
              const groupImages = group.attachments.filter(att => att.is_image)
              const groupDocs = group.attachments.filter(att => !att.is_image)

              return (
                <div key={group.key} className="bg-white rounded-lg border border-gray-200 p-3">
                  {/* Group header */}
                  <div className="mb-2">
                    {group.level === 'header' ? (
                      <h4 className="text-sm font-medium text-gray-700">
                        Order Level
                        <span className="ml-2 text-gray-400 font-normal">
                          ({group.attachments.length} {group.attachments.length === 1 ? 'attachment' : 'attachments'})
                        </span>
                      </h4>
                    ) : (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">
                          Line {group.line_num}
                          {group.part_num && (
                            <span className="ml-2 font-normal text-gray-500">
                              {group.part_num}
                            </span>
                          )}
                          <span className="ml-2 text-gray-400 font-normal">
                            ({group.attachments.length} {group.attachments.length === 1 ? 'attachment' : 'attachments'})
                          </span>
                        </h4>
                        {/* Show XPartNum (customer part number) if different from PartNum */}
                        {group.xpart_num && group.xpart_num !== group.part_num && (
                          <p className="text-xs text-blue-600 mt-0.5">
                            Customer P/N: {group.xpart_num}
                          </p>
                        )}
                        {group.line_desc && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">
                            {group.line_desc}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Images as grid */}
                  {groupImages.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
                      {groupImages.map((att) => (
                        <ImageThumbnail
                          key={att.xfile_ref_num}
                          attachment={att}
                          onClick={() => setSelectedAttachment(att)}
                          isSelected={selectedForEmail.some(p => p.xfile_ref_num === att.xfile_ref_num)}
                          onSelect={toggleSelect}
                          showSelect={true}
                        />
                      ))}
                    </div>
                  )}

                  {/* Non-images as compact list */}
                  {groupDocs.length > 0 && (
                    <div className="space-y-1">
                      {groupDocs.map((att) => (
                        <CompactAttachmentRow
                          key={att.xfile_ref_num}
                          attachment={att}
                          onClick={() => setSelectedAttachment(att)}
                          isSelected={selectedForEmail.some(p => p.xfile_ref_num === att.xfile_ref_num)}
                          onSelect={toggleSelect}
                          showSelect={true}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : viewMode === 'grid' ? (
          <div>
            {/* Image grid */}
            {imageAttachments.length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-medium text-gray-500 mb-2">
                  Images ({imageAttachments.length})
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  {imageAttachments.map((att) => (
                    <ImageThumbnail
                      key={att.xfile_ref_num}
                      attachment={att}
                      onClick={() => setSelectedAttachment(att)}
                      isSelected={selectedForEmail.some(p => p.xfile_ref_num === att.xfile_ref_num)}
                      onSelect={toggleSelect}
                      showSelect={true}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* Non-images as list */}
            {nonImageAttachments.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-2">
                  Documents ({nonImageAttachments.length})
                </h4>
                <div className="space-y-1">
                  {nonImageAttachments.map((att) => (
                    <CompactAttachmentRow
                      key={att.xfile_ref_num}
                      attachment={att}
                      onClick={() => setSelectedAttachment(att)}
                      isSelected={selectedForEmail.some(p => p.xfile_ref_num === att.xfile_ref_num)}
                      onSelect={toggleSelect}
                      showSelect={true}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* List view */
          <div className="space-y-1">
            {filteredAttachments.map((att) => (
              <CompactAttachmentRow
                key={att.xfile_ref_num}
                attachment={att}
                onClick={() => setSelectedAttachment(att)}
                isSelected={selectedForEmail.some(p => p.xfile_ref_num === att.xfile_ref_num)}
                onSelect={toggleSelect}
                showSelect={true}
              />
            ))}
          </div>
        )}

        {/* Selection hint - show when nothing selected */}
        {selectedForEmail.length === 0 && (
          <p className="text-xs text-gray-400 text-center mt-3">
            Click the checkbox to select files for email
          </p>
        )}
      </div>

      {/* Viewer modal */}
      {selectedAttachment && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] overflow-hidden">
            <AttachmentViewer
              xfileRefNum={selectedAttachment.xfile_ref_num}
              metadata={{
                ...selectedAttachment,
                view_url: `/api/attachments/file/${selectedAttachment.xfile_ref_num}`,
                download_url: `/api/attachments/file/${selectedAttachment.xfile_ref_num}?download=true`,
              }}
              onClose={() => setSelectedAttachment(null)}
            />
          </div>
        </div>
      )}

      {/* Email Compose Modal */}
      <EmailComposeModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        orderNum={getOrderNum()}
        selectedPhotos={selectedForEmail}
        onSuccess={handleEmailSuccess}
      />

      {/* Upload Modal */}
      {showUploadModal && tableName && recordKey1 && (
        <UploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          tableName={tableName}
          recordKey1={recordKey1}
          recordKey2={recordKey2}
          title={title}
        />
      )}
    </div>
  )
}


/**
 * UploadModal - Upload files to Epicor and attach to a record
 */
function UploadModal({ isOpen, onClose, tableName, recordKey1, recordKey2, title }) {
  const [file, setFile] = useState(null)
  const [description, setDescription] = useState('')
  const [docTypeId, setDocTypeId] = useState('')
  const [docTypes, setDocTypes] = useState([])
  const [loadingDocTypes, setLoadingDocTypes] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState(null)
  const [error, setError] = useState(null)
  const [isDragging, setIsDragging] = useState(false)

  const fileInputRef = useState(null)

  // Allowed file types
  const ALLOWED_EXTENSIONS = [
    '.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp',
    '.xlsx', '.xls', '.csv', '.doc', '.docx',
    '.dwg', '.dxf', '.step', '.stp', '.igs',
    '.msg', '.eml', '.txt', '.zip', '.tif', '.tiff', '.bmp',
  ]
  const MAX_FILE_SIZE_MB = 25

  // Fetch document types on mount
  useEffect(() => {
    if (!isOpen) return
    const token = localStorage.getItem('amtrend_token')
    fetch('/api/attachments/doc-types', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        setDocTypes(data.document_types || [])
        setLoadingDocTypes(false)
      })
      .catch(err => {
        console.error('Failed to load doc types:', err)
        setLoadingDocTypes(false)
      })
  }, [isOpen])

  const validateFile = (f) => {
    const ext = '.' + f.name.split('.').pop().toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type "${ext}" is not allowed`
    }
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File is too large (${(f.size / 1024 / 1024).toFixed(1)}MB). Maximum is ${MAX_FILE_SIZE_MB}MB.`
    }
    return null
  }

  const handleFileSelect = (f) => {
    setError(null)
    setUploadResult(null)
    const validationError = validateFile(f)
    if (validationError) {
      setError(validationError)
      return
    }
    setFile(f)
    // Auto-fill description from filename (without extension)
    if (!description) {
      const name = f.name.replace(/\.[^/.]+$/, '')
      setDescription(name)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) handleFileSelect(droppedFile)
  }

  const handleUpload = async () => {
    if (!file || !docTypeId || !description) return

    setUploading(true)
    setError(null)

    try {
      const token = localStorage.getItem('amtrend_token')
      const formData = new FormData()
      formData.append('file', file)
      formData.append('table', tableName)
      formData.append('key1', recordKey1)
      formData.append('doc_type_id', docTypeId)
      formData.append('description', description)
      if (recordKey2) formData.append('key2', recordKey2)

      const response = await fetch('/api/attachments/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      })

      const result = await response.json()

      if (!response.ok || result.error) {
        throw new Error(result.error || result.detail || 'Upload failed')
      }

      setUploadResult(result)
    } catch (err) {
      setError(err.message)
    } finally {
      setUploading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-medium text-gray-800 flex items-center gap-2">
            <UploadCloud size={18} className="text-green-600" />
            Upload Attachment
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Target record */}
          <div className="text-sm text-gray-600 bg-gray-50 rounded p-2">
            Attaching to: <span className="font-medium">{tableName}/{recordKey1}{recordKey2 ? `/${recordKey2}` : ''}</span>
          </div>

          {/* Success message */}
          {uploadResult && (
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
              <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Upload successful!</p>
                <p className="text-green-600">{uploadResult.filename} attached (#{uploadResult.attach_num})</p>
                <button
                  onClick={() => { setFile(null); setUploadResult(null); setDescription(''); }}
                  className="mt-2 text-xs underline text-green-700 hover:text-green-900"
                >
                  Upload another file
                </button>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {!uploadResult && (
            <>
              {/* File drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById('upload-file-input')?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${isDragging ? 'border-green-400 bg-green-50' : file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
              >
                <input
                  id="upload-file-input"
                  type="file"
                  className="hidden"
                  accept={ALLOWED_EXTENSIONS.join(',')}
                  onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                />
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileTypeIcon extension={file.name.split('.').pop()} className="w-6 h-6" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-800">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null) }}
                      className="ml-2 text-gray-400 hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <UploadCloud size={32} className="mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-600">Drop a file here or click to browse</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, images, Office docs, CAD files (max {MAX_FILE_SIZE_MB}MB)</p>
                  </>
                )}
              </div>

              {/* Document Type dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                {loadingDocTypes ? (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Loader2 size={14} className="animate-spin" /> Loading types...
                  </div>
                ) : (
                  <select
                    value={docTypeId}
                    onChange={(e) => setDocTypeId(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select a document type...</option>
                    {docTypes.map(dt => (
                      <option key={dt.doc_type_id} value={dt.doc_type_id}>
                        {dt.doc_type_id} — {dt.description}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Description / Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description / Label</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., Customer PO, Shop Drawing Rev B, Material Certificate"
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            {uploadResult ? 'Close' : 'Cancel'}
          </button>
          {!uploadResult && (
            <button
              onClick={handleUpload}
              disabled={!file || !docTypeId || !description || uploading}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={14} />
                  Upload
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}


// Compact row for list view - supports selection for email
function CompactAttachmentRow({ attachment, onClick, isSelected, onSelect, showSelect = false }) {
  const { filename, extension, doc_type_id, is_image, is_pdf } = attachment
  const is_msg = extension?.toLowerCase() === 'msg'
  const can_preview = is_image || is_pdf || is_msg

  return (
    <div className="flex items-center gap-1">
      {/* Selection checkbox */}
      {showSelect && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onSelect?.(attachment)
          }}
          className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
            isSelected
              ? 'bg-amtrend-blue border-amtrend-blue text-white'
              : 'bg-white border-gray-300 text-transparent hover:border-amtrend-blue'
          }`}
        >
          <Check size={12} />
        </button>
      )}

      <button
        onClick={() => onClick?.(attachment)}
        className={`flex items-center gap-2 p-2 flex-1 text-left bg-white border rounded hover:border-amtrend-blue hover:bg-blue-50/30 transition-all ${
          isSelected ? 'border-amtrend-blue bg-blue-50/20' : 'border-gray-100'
        }`}
      >
        <FileTypeIcon extension={extension} className="w-5 h-5 flex-shrink-0" />
        <span className="flex-1 text-sm text-gray-800 truncate">{filename}</span>
        {doc_type_id && (
          <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded flex-shrink-0">
            {doc_type_id}
          </span>
        )}
        {can_preview && (
          <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" title="Preview available" />
        )}
      </button>
    </div>
  )
}

// Image thumbnail for grid view - supports selection for email
function ImageThumbnail({ attachment, onClick, isSelected, onSelect, showSelect = false }) {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)
  const [blobUrl, setBlobUrl] = useState(null)

  useEffect(() => {
    let mounted = true
    const loadImage = async () => {
      try {
        const token = localStorage.getItem('amtrend_token')
        const response = await fetch(`/api/attachments/image/${attachment.xfile_ref_num}?thumbnail=true&max_size=200`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (!response.ok) throw new Error('Failed to load')
        const blob = await response.blob()
        if (mounted) {
          setBlobUrl(URL.createObjectURL(blob))
          setLoaded(true)
        }
      } catch (err) {
        if (mounted) setError(true)
      }
    }
    loadImage()
    return () => {
      mounted = false
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [attachment.xfile_ref_num])

  return (
    <div className="relative group">
      {/* Selection checkbox - only show when showSelect is true */}
      {showSelect && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onSelect?.(attachment)
          }}
          className={`absolute top-1 left-1 z-10 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            isSelected
              ? 'bg-amtrend-blue border-amtrend-blue text-white'
              : 'bg-white/80 border-gray-300 text-transparent hover:border-amtrend-blue'
          }`}
        >
          <Check size={12} />
        </button>
      )}

      <button
        onClick={() => onClick?.(attachment)}
        className={`relative aspect-square bg-gray-100 rounded overflow-hidden border transition-colors ${
          isSelected ? 'border-amtrend-blue border-2' : 'border-gray-200 hover:border-amtrend-blue'
        }`}
      >
        {!loaded && !error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Image className="w-6 h-6 text-gray-300" />
          </div>
        )}
        {blobUrl && (
          <img
            src={blobUrl}
            alt={attachment.filename}
            className="w-full h-full object-cover"
          />
        )}
        {attachment.doc_type_id && (
          <span className="absolute bottom-1 left-1 text-[10px] px-1 py-0.5 bg-black/60 text-white rounded">
            {attachment.doc_type_id}
          </span>
        )}
      </button>
    </div>
  )
}
